/**
 * The features an administrator switches on for one person (Oct 2026).
 *
 * The features of `USER_FEATURES` in
 * `optivo-service/src/services/core/userFeatures.ts` that this page gates a
 * screen by. A feature is an entitlement only an administrator writes — on the
 * admin console's Features page, or its Users console — and it is off until
 * one does. It is not the person's own preference: "Allow AI agents to
 * connect" on the AI agents section is that, and only means something once
 * the feature is on.
 *
 * The page gates by two: `mcp`, the AI agents and the security tokens they
 * connect with, and `lighthouse`, competitor prices read from the property's
 * own Lighthouse account (the choice on the competitor tracking card). The
 * server rolls out the Ask Optivo assistant the same way (`assistant`), and
 * says whether to offer it on the Ask Optivo status instead, so `/api/me`
 * naming it is read past here like any key this list does not hold. While
 * `mcp` is off the account page offers neither section and the server
 * refuses their routes; the person's tokens are kept, and work again once it
 * is switched back on.
 */
export const USER_FEATURES = ["mcp", "lighthouse"] as const;

export type UserFeature = (typeof USER_FEATURES)[number];

export function isUserFeature(value: unknown): value is UserFeature {
    return typeof value === "string" && (USER_FEATURES as readonly string[]).includes(value);
}

/**
 * The features as the server names them, read defensively: the page and the
 * service deploy separately. A server that names none predates the switch and
 * reads as none on — the conservative reading, nothing being on until an
 * administrator turns it on — and a feature this page does not know is left
 * out rather than matched to something it is not. Each once, in this list's
 * order.
 */
export function readUserFeatures(value: unknown): UserFeature[] {
    if (!Array.isArray(value)) return [];
    return USER_FEATURES.filter((feature) => value.includes(feature));
}

/**
 * The server's refusal of a route behind a feature the person does not have —
 * `403 { code: "FEATURE_NOT_ENABLED", feature }` from
 * `optivo-service/src/middleware/userFeature.ts`. Thrown by the clients of the
 * routes behind one (`apiTokensApi.ts`, `accountMcpApi.ts`) apart from every
 * other failure, because it is no failure: an administrator switched the
 * feature off while the page was open, and the section says so
 * (`FeatureNotEnabledNotice`) rather than offering a retry that cannot work.
 */
export class FeatureNotEnabledError extends Error {
    readonly code = "FEATURE_NOT_ENABLED" as const;
    /** The feature named, read as the AI agents where the server names one this page does not know. */
    readonly feature: UserFeature;

    constructor(feature: UserFeature, message = "This feature is not switched on for this account.") {
        super(message);
        this.name = "FeatureNotEnabledError";
        this.feature = feature;
    }
}

/**
 * The refusal an answer carries, if it is the server's refusal for a feature
 * switched off: a 403 naming `FEATURE_NOT_ENABLED`, and nothing else — another
 * 403 is another refusal. Null for every other answer.
 */
export function featureRefusalOf(status: number, body: unknown): FeatureNotEnabledError | null {
    if (status !== 403 || !body || typeof body !== "object") return null;
    const answer = body as Record<string, unknown>;
    if (answer.code !== "FEATURE_NOT_ENABLED") return null;
    const feature = isUserFeature(answer.feature) ? answer.feature : USER_FEATURES[0];
    return new FeatureNotEnabledError(feature, typeof answer.error === "string" && answer.error ? answer.error : undefined);
}
